import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	Dialog,
	FormattedText,
	NoResults,
	SearchInputForm,
	Typography
} from "client/Components";
import styles from "./PlusMenu.module.less";
import { Category } from "./Category";
import { GlobalCreationItem, GlobalCreationItemKind } from "client/Api/GlobalCreation/globalCreation";
import { isNil, isNotNil } from "client/Misc/utils";
import { groupBy } from "client_legacy/ndaw/arrayHelpers";
import { CreationCardItem } from "./CreationCard";
import { getGlobalCreationItemConfiguration } from "./common";
import { contains, normalizeForSearch } from "client/Data/searching";
import { useCreationHandler, useGlobalCreationItems } from "./hooks";
import { fromArray } from "client_legacy/ndaw/mapHelpers";

export type PlusMenuProps = {
	onClose(): void;
};

export const PlusMenu = (props: PlusMenuProps): ReactElement => {
	const {
		onClose
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");

	const allItems: readonly CreationCardItem[] | undefined = useGlobalCreationItems(converter);
	const [openedElement, open] = useCreationHandler();

	const categoriesToRender = useMemo(
		(): readonly CategoryToRender[] => {
			if (!allItems) {
				return [];
			}

			const categoryNameMap: Map<GlobalCreationItemKind, string> = fromArray(
				categoryConfigurations,
				(x) => x.kind,
				(x) => x.name
			);

			const normalizedSearchTerm: string = normalizeForSearch(searchTerm);
			const matchingItems: readonly CreationCardItem[] = allItems
				.filter(
					(x) => contains(x.name, normalizedSearchTerm)
						|| contains(
							categoryNameMap.get(
								x.originalItem
									.kind
							),
							normalizedSearchTerm
						)
				);

			const categories: Partial<Record<GlobalCreationItemKind, readonly CreationCardItem[]>> = groupBy(
				matchingItems,
				(x) => x.originalItem
					.kind
			);

			return categoryConfigurations
				.map(
					(x): CategoryToRender | null => {
						const items: readonly CreationCardItem[] | undefined = categories[x.kind];
						if (!items) {
							return null;
						}

						return {
							...x,
							items
						};
					}
				)
				.filter(isNotNil);
		},
		[allItems, searchTerm]
	);

	const openHandler = useCallback(
		async (item: CreationCardItem): Promise<void> => {
			await open(item.originalItem, onClose);
		},
		[open, onClose]
	);

	if (openedElement) {
		return openedElement;
	}

	return (
		<Dialog
			variant="left-slider"
			trackingId="plus-menu-slider"
			hasBusyIndicator={isNil(allItems)}
			onClose={onClose}
		>
			<Dialog.Header
				title={localize("_CreateNewItem")}
				onClose={onClose}
				hasBorder
			/>

			<Dialog.Body>
				<div className={styles.root}>
					<SearchInputForm
						trackingId="search-input"
						onChange={setSearchTerm}
						placeholder={<SearchPlaceholder />}
						icon="filter"
						hasMarginBottom
						height="large"
					/>

					<div className={styles.scrollArea}>
						{allItems && categoriesToRender.length === 0 && (
							<NoResults
								title={localize(searchTerm ? "_NoSearchResults" : "_NoAccess")}
								icon={searchTerm ? "magnifying-glass" : "lock"}
								message={localize(
									searchTerm
										? "_PleaseTrySearchingWithAnotherTerm"
										: "_YouDontHaveAccessToCreateAnItem"
								)}
							/>
						)}

						{categoriesToRender.map((x): ReactElement => (
							<Category
								key={x.trackingId}
								name={x.name}
								trackingId={x.trackingId}
								items={x.items}
								onOpen={openHandler}
							/>
						))}
					</div>
				</div>
			</Dialog.Body>
		</Dialog>
	);
};
PlusMenu.displayName = "PlusMenu";

function converter(item: GlobalCreationItem): CreationCardItem {
	const {
		nameKey,
		icon,
		trackingId
	} = getGlobalCreationItemConfiguration(item);

	return {
		name: localize(nameKey),
		icon,
		trackingId,
		originalItem: item
	};
}

type CategoryConfiguration = {
	kind: GlobalCreationItemKind;
	name: string;
	trackingId: string;
};

export const categoryConfigurations: readonly CategoryConfiguration[] = [
	{
		kind: GlobalCreationItemKind.General,
		name: localize("_General"),
		trackingId: "general"
	},
	{
		kind: GlobalCreationItemKind.Hub,
		name: localize("_TheHub"),
		trackingId: "hub"
	},
	{
		kind: GlobalCreationItemKind.File,
		name: localize("_Files"),
		trackingId: "files"
	}
];

type CategoryToRender = {
	kind: GlobalCreationItemKind;
	name: string;
	trackingId: string;
	items: readonly CreationCardItem[];
};

const SearchPlaceholder = (): ReactElement => {
	return (
		<span>
			<FormattedText
				text={localize("_FilterByProperties")}
				thingsToReplace={[
					{
						what: "{{Properties}}",
						filler: (
							<Typography weight="bold" >
								<i>{localize("_ItemAndCategoryName")}</i>
							</Typography>
						)
					}
				]}
			/>
		</span>
	);
};
SearchPlaceholder.displayName = "SearchPlaceholder";
